<script setup lang="ts">
import { useFrontlineFiltersStore } from '@Portal/stores/frontlineFilters';
import { ProcessFlowCard } from '@Shared/types/portal/ProcessFlow';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isToday from 'dayjs/plugin/isToday';
import SummaryCard from './parts/SummaryCard.vue';

dayjs.extend(isoWeek);
dayjs.extend(isToday);

// it's a bit messy to propagate the module id like this,
// but realistically this is the same info that a url redirect would have
const emit = defineEmits<{
  (event: 'cardClick', card: ProcessFlowCard, processFlowModuleId: string): void;
  (event: 'loadPage', page: number, mergeResultsKey?: string, feedTab?: string, sortOrder?: string): void;
}>();

const props = defineProps<{
  title: string;
  pageSize: number;
  processFlowModuleId: string;
  cards: ProcessFlowCard[];
  count: number;
  smartDataModuleId: string;
}>();

const { pageSize, processFlowModuleId, cards, count } = toRefs(props);

const category = (card: ProcessFlowCard) => {
  if (!card.responded.length || !card.responded[0]) return 'Older';

  const date = dayjs(card.responded[0].value);
  const now = dayjs(import.meta.env.VITE_DATE_ANCHOR);

  if (now.year() == date.year() && now.month() == date.month() && now.day() == date.day()) {
    return 'Today';
  } else if (now.year() == date.year() && now.isoWeek() == date.isoWeek()) {
    return 'This Week';
  } else if (now.year() == date.year() && now.month() == date.month()) {
    return 'This Month';
  } else {
    return 'Older';
  }
};

const categoriseCards = computed(() => {
  const groups: Record<string, ProcessFlowCard[]> = {};

  props.cards.forEach((card) => {
    const cardCategory = category(card);
    if (cardCategory in groups) {
      groups[cardCategory].push(card);
    } else {
      groups[cardCategory] = [card];
    }
  });
  return groups;
});

const loadMore = () => {
  // presuming that we load pageSize each time the next index will be cards.length / pageSize
  // i.e. if pageSize = 20, and cards.length = 100 we've loaded pages 0 -> 4 (5 pages worth)
  // and next one to load is page 5
  const pageIndex = Math.floor(cards.value.length / pageSize.value);
  useFrontlineFiltersStore().trackEvent('ProcessFlowFeed-LoadMore', {
    page: String(pageIndex),
  });

  emit('loadPage', pageIndex, 'cards', undefined, undefined);
};
</script>
<template>
  <div class="overflow-auto hide-scrollbar-auto flex flex-col px-3" id="processflow-feed-cards-container">
    <div
      class="flex flex-col gap-2 xs:pb-12 xl:pb-2"
      v-for="category in Object.keys(categoriseCards)"
      v-bind:key="category"
    >
      <div class="pt-6 uppercase color-[#4F4F4F] text-3">{{ category }}</div>
      <template v-for="card in categoriseCards[category]" v-bind:key="card.id">
        <SummaryCard
          @card-click="(card) => emit('cardClick', card, processFlowModuleId)"
          :card="card"
          :smart-data-module-id="smartDataModuleId"
        />
      </template>
    </div>
    <div class="w-full flex flex-row justify-center pt-0 py-12 xs:pb-32 xl:pb-2" v-if="cards.length < count">
      <span
        class="cursor-pointer text-[#C73362] underline xl:text-4 xs:text-5 font-family[Roboto]"
        id="processflow-feed-load-more"
        @click="loadMore()"
      >
        Load More
      </span>
    </div>
  </div>
</template>
